import React from "react";
import BottomSheet from "./BottomSheet";
import Modal from "./Modal";
import { useIsMobile } from "./useIsMobile";

export interface ActionDialogProps {
  /** Heads the panel, and names it to a screen reader. */
  title: string;
  onClose: () => void;
  /**
   * The actions, as a fragment of two buttons — the decisive one last.
   *
   * Each shell lays them out the way its own surface wants: against the right
   * edge in the dialog, and as equal halves of the row in the sheet, which is
   * what puts both of them under a thumb. Neither is the caller's business.
   */
  footer: React.ReactNode;
  /** What the panel is asking about. The sheet pads it to its own gutter. */
  children: React.ReactNode;
  /** The dialog's width, from `md` up — `max-w-[440px]` and the like. Ignored
   *  on a phone, where a sheet is as wide as the screen. */
  className?: string;
  /**
   * Whether a press outside closes it. On by default, which is what an
   * ordinary dialog does — turn it off for one that is mid-request, where a
   * stray press would leave nothing on screen to say the work is still going.
   */
  dismissable?: boolean;
}

/**
 * A short dialog that asks for one decision — as a centred panel on a wide
 * screen, and as a sheet up from the bottom edge on a phone.
 *
 * Not one panel with different corners. A dialog centred in a phone's viewport
 * is read at arm's length from the thumb that has to answer it, and its two
 * buttons end up wherever their own words put them; a sheet arrives under that
 * thumb with the answers as equal halves of its foot. They are different
 * elements with different ways in and out, so only one of them is ever built.
 *
 * This exists because four dialogs were about to make the same choice by hand
 * — the two payment answers, the request that raises one, and the
 * pre-consultation review. Four copies of a width check is four places for the
 * two shells to drift apart, and the drift is invisible until somebody opens
 * the odd one out on a phone.
 *
 * For a dialog that asks one thing. A panel with a heading of its own, a body
 * that has to scroll under pinned actions, or a layout that differs between the
 * two shells wants `Modal` and `BottomSheet` directly — see
 * `PreconsultationReviewModal`, which carries a status pill beside its name and
 * so cannot hand its title over as a string.
 */
const ActionDialog: React.FC<ActionDialogProps> = ({
  title,
  onClose,
  footer,
  children,
  className,
  dismissable = true,
}) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <BottomSheet
        open
        title={title}
        // The sheet closes on a press outside it and on Escape; where the
        // caller is holding it open, both land on a close that does nothing.
        onClose={dismissable ? onClose : () => {}}
        footer={footer}
      >
        {/* The sheet's own gutter, which its heading above is already set to.
            The body is left unpadded by `BottomSheet` because what goes in it
            is as often a full-width list of rows as it is a paragraph. */}
        <div className="px-5">{children}</div>
      </BottomSheet>
    );
  }

  return (
    <Modal
      open
      title={title}
      onClose={onClose}
      className={className}
      dismissOnBackdrop={dismissable}
      footer={footer}
    >
      {children}
    </Modal>
  );
};

export default ActionDialog;
